/**
 * Active SaaS-AI vendor — module-level source of truth for the API client.
 *
 * saasAiClient resolves its path prefix (/<vendor>/api/v1) from getActiveVendor()
 * at call time. SaasVendorProvider keeps this in sync with the ?vendor= URL param
 * (and persists the last selection so internal navigations that drop the param
 * still render the right vendor).
 *
 * Adding a vendor = one entry in SAAS_VENDORS (its `module` slug gates the
 * selector by org entitlement). The backend serves every vendor from the same
 * core, so no client changes are needed.
 */

export const SAAS_VENDORS = [
  { id: 'anthropic', label: 'Anthropic', module: 'anthropic' },
  { id: 'cursor', label: 'Cursor', module: 'cursor' },
  { id: 'openai', label: 'OpenAI', module: 'openai' },
  { id: 'google_antigravity', label: 'Google Antigravity', module: 'google_antigravity' },
];

const DEFAULT_VENDOR = 'anthropic';

let _vendor = DEFAULT_VENDOR;

export function isValidVendor(v) {
  return SAAS_VENDORS.some((x) => x.id === v);
}

export function getActiveVendor() {
  return _vendor;
}

export function setActiveVendor(v) {
  if (isValidVendor(v)) _vendor = v;
}

export function getVendorMeta(v = _vendor) {
  return SAAS_VENDORS.find((x) => x.id === v) || SAAS_VENDORS[0];
}

/**
 * Surface per vendor. Anthropic keeps its Chat / Code / Cowork surfaces;
 * OpenAI gains ChatGPT / Codex CLI. Other vendors have no surfaces
 * (empty list = no selector, no ?surface= param).
 *
 * saasAiClient appends ?surface= to every request when the active vendor has
 * the selected surface.
 */
export const SAAS_SURFACES = {
  anthropic: [
    { id: 'all', label: 'All' },
    { id: 'chat', label: 'Claude Chat' },
    { id: 'code', label: 'Claude Code' },
    { id: 'cowork', label: 'Claude Cowork' },
  ],
  openai: [
    { id: 'all', label: 'All' },
    { id: 'chat', label: 'ChatGPT' },
    { id: 'code', label: 'Codex CLI' },
  ],
  google_antigravity: [
    { id: 'all', label: 'All' },
    { id: 'desktop', label: 'Desktop App' },
    { id: 'ide', label: 'IDE' },
    { id: 'cli', label: 'CLI' },
    { id: 'web_remote', label: 'Web Remote' },
  ],
};

let _surface = 'all';

export function getSurfacesForVendor(v = _vendor) {
  return SAAS_SURFACES[v] || [];
}

export function hasVendorSurfaces(v = _vendor) {
  return getSurfacesForVendor(v).length > 0;
}

export function isValidSurface(s, vendor = _vendor) {
  return getSurfacesForVendor(vendor).some((x) => x.id === s);
}

export function getActiveSurface() {
  return _surface;
}

export function setActiveSurface(s) {
  if (isValidSurface(s)) _surface = s;
}

/**
 * Agent / user scope (2026-10-11) — the AI Providers module's agent and user
 * Filters (the page filter bar, ProviderScopeBar; one selection per vendor). saasAiClient appends
 * it to every request as `?scope_agent=` / `?scope_user=` (comma-joined), and
 * the saas-ai service narrows every list, KPI and aggregate to it server-side.
 *
 * SaasEntityScope (components/saas-ai) writes it, and only while a page that
 * draws the pickers is open: a page without them is never narrowed by a
 * selection it does not show. The scope belongs to one vendor, so a call that
 * names another vendor (the Inbox, a recommendation's evidence) never carries it.
 */
let _entityScope = null;

const cleanList = (list) => (Array.isArray(list)
  ? [...new Set(list.map((v) => String(v ?? '').trim()).filter(Boolean))]
  : []);

export function setEntityScope(scope) {
  if (!scope || !scope.vendor) {
    _entityScope = null;
    return;
  }
  _entityScope = { vendor: scope.vendor, agents: cleanList(scope.agents), users: cleanList(scope.users) };
}

export function getEntityScope() {
  return _entityScope;
}

/** `{ scope_agent?, scope_user? }` for a request to `vendor`; `{}` when nothing is selected. */
export function getEntityScopeParams(vendor = _vendor) {
  const s = _entityScope;
  if (!s || s.vendor !== vendor) return {};
  const params = {};
  if (s.agents.length) params.scope_agent = s.agents.join(',');
  if (s.users.length) params.scope_user = s.users.join(',');
  return params;
}
